/**
 * Default salary currency for a new offer (CORE-4740).
 *
 * Resolved client-side from the job payload already in Redux
 * (`currentJob.data`), mirroring how `ats_office_id` is prefilled in
 * `offerFormHelpers`. There is no backend default for the web path: the form
 * always sends `salary_currency_id`, so a server-side default would never fire.
 *
 * Where the office is decides the currency. With no location on that office the
 * field opens blank rather than on a wrong default.
 */

export interface JobOffice {
  id?: number;
  /**
   * The office's country, and the only thing consulted for the currency.
   *
   * It shares an id space with `GET /compensations/currencies` — an office in
   * Chennai carries 101, which is that list's "Indian rupee (INR)" row. The
   * list is deduplicated to one row per currency though, so only one country
   * represents each: a US office carries 96, which is NOT in the list, and
   * resolves to nothing rather than to a wrong currency.
   */
  places_country_id?: number | null;
}

export interface JobForCurrency {
  id?: number | string | null;
  basic_info?: {
    office?: JobOffice[] | null;
  } | null;
}

const isUsable = (id: unknown): id is number =>
  typeof id === 'number' && Number.isFinite(id);

/**
 * The Redux job, but only when it is actually THIS offer's job.
 *
 * `currentJob` is a single shared slot written by the job-details and
 * job-creation screens (`getCurrentJob`); nothing in the candidate drawer
 * refreshes it. Open an offer from a candidate profile, global search or a
 * dashboard task and that slot still holds whichever job was last visited — so
 * reading a currency off it can prefill a DIFFERENT job's currency, which is
 * worse than not prefilling at all.
 *
 * The application payload carries only `ats_job: { id, name }`, which is enough
 * to tell the two apart. A missing or mismatched id yields `undefined`, and the
 * caller falls through to the USD floor — the behaviour before CORE-4740.
 */
export const jobForApplication = (
  currentJob?: JobForCurrency | null,
  applicationJobId?: number | string | null
): JobForCurrency | undefined => {
  if (!currentJob || !applicationJobId || !currentJob.id) return undefined;
  return String(currentJob.id) === String(applicationJobId)
    ? currentJob
    : undefined;
};

/**
 * First candidate that is both set and offered by the currency list.
 *
 * `allowedCurrencyIds` is the id set from `GET /compensations/currencies`,
 * which is deduplicated to one row per ISO code — several countries share a
 * currency but only one id per currency is selectable, and the backend rejects
 * any other country id. An id outside that set would render the select blank
 * and 404 on save, so such a candidate is skipped rather than used.
 *
 * When the list hasn't loaded yet the guard is skipped entirely: an empty set
 * means "unknown", not "nothing is allowed".
 */
export const firstAllowedCurrencyId = (
  candidates: Array<number | null | undefined>,
  allowedCurrencyIds?: Set<number>
): number | undefined => {
  const hasAllowList = !!allowedCurrencyIds && allowedCurrencyIds.size > 0;
  return candidates.find(
    (id): id is number =>
      isUsable(id) && (!hasAllowList || allowedCurrencyIds.has(id))
  );
};

/**
 * Resolution order, first non-null wins:
 *
 *   1. `basic_info.office[selected].places_country_id` — where the role is based
 *   2. `undefined`                                     — the field opens blank
 *
 * Office-led, matching the ticket: where the role is based decides the payroll
 * currency. The job's own salary-band currency
 * (`compensation.base_salary.currency_country_id`) is deliberately NOT read —
 * a job posted in one currency does not imply the offer is paid in it.
 *
 * Rungs 1-2 read the per-office `compensation` block, which — unlike the
 * top-level one — is not gated on `compensation.enabled`, so they still
 * resolve for accounts with compensation switched off.
 *
 * `undefined` means "the office configures nothing", and the form leaves the
 * field blank for the recruiter to pick. There is deliberately no USD floor —
 * a confident-but-wrong default is what gets silently accepted.
 */
export const resolveOfferDefaultCurrencyId = ({
  currentJob,
  selectedOfficeId,
  allowedCurrencyIds,
}: {
  currentJob?: JobForCurrency | null;
  /** The office the offer form prefilled; falls back to the job's first. */
  selectedOfficeId?: number | null;
  allowedCurrencyIds?: Set<number>;
}): number | undefined =>
  resolveOfficeCurrencyId({
    offices: currentJob?.basic_info?.office,
    selectedOfficeId,
    allowedCurrencyIds,
  });

/**
 * The office rungs, shared with the requisition form.
 *
 * Offers read the offices off the job; requisitions read them off their own
 * office picker. Either way the question is the same — given these offices and
 * the one that is selected, which currency does the role pay in?
 *
 *   1. the selected office's `places_country_id` — where the role is based
 *   2. `undefined` — no location on that office, so the form leaves it blank
 *
 * Location, not the currency configured against the office in
 * Settings -> Compensation. Those disagree in practice: a Paris office with no
 * location at all was configured for INR there, so reading the configuration
 * put rupees on a French offer. Where the role sits is the question being
 * asked, so that is what gets read.
 *
 * The trade-off is that only one country represents each currency in the list,
 * so an office in a country that is not the representative resolves to nothing
 * — a US office (96) is not in the list and opens blank. Blank is the intended
 * failure: the recruiter picks, rather than accepting a wrong currency.
 *
 * `selectedOfficeId` accepts an array because the requisition picker is
 * multi-select; the first entry wins, matching how the offer form treats the
 * job's office list.
 *
 * `fallbackToFirstOffice` governs every use of "the first office", and the two
 * callers need opposite answers.
 *
 * For an offer, `offices` IS the job's own list: the first entry is related to
 * the role, and the form prefills it when nothing else is selected, so reading
 * a currency off it is reasonable.
 *
 * For a requisition, `offices` is every office in the org. The first entry is
 * whichever one happens to sort first and has nothing to do with the role — a
 * Paris office with no country of its own would silently inherit Bangalore's
 * INR. So requisitions pass `false`, which drops the fallback from BOTH the
 * office lookup and rung 3, and the field stays blank for the creator to pick.
 */
export const resolveOfficeCurrencyId = ({
  offices,
  selectedOfficeId,
  allowedCurrencyIds,
  fallbackToFirstOffice = true,
}: {
  offices?: JobOffice[] | null;
  selectedOfficeId?: number | number[] | null;
  allowedCurrencyIds?: Set<number>;
  fallbackToFirstOffice?: boolean;
}): number | undefined => {
  const list = offices || [];
  const wanted = Array.isArray(selectedOfficeId)
    ? selectedOfficeId[0]
    : selectedOfficeId;

  const explicitOffice = isUsable(wanted)
    ? list.find((office) => office?.id === wanted)
    : undefined;

  if (!explicitOffice && !fallbackToFirstOffice) return undefined;

  const selectedOffice = explicitOffice || list[0];

  // Only the office in play. An office with no location resolves to nothing
  // rather than borrowing a neighbour's — that borrowing is what put Bangalore's
  // rupees on a Paris requisition.
  return firstAllowedCurrencyId(
    [selectedOffice?.places_country_id],
    allowedCurrencyIds
  );
};

/** Currency-list rows → the id set the guard above checks against. */
export const toAllowedCurrencyIds = (
  currencies?: Array<{ id?: number | null }> | null
): Set<number> =>
  new Set((currencies || []).map((c) => c?.id).filter(isUsable));
